/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { MED_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Typography2 as Typography } from '@coursera/cds-core';
import { StarFilledIcon } from '@coursera/cds-icons';

import type { TopReview } from 'bundles/reviews-common/types/reviewsSharedTypes';

import _t from 'i18n!nls/reviews';

const styles = {
  card: css`
    width: 100%;
    min-width: 0;
    flex: 1;
    min-height: 0;
    padding: var(--cds-spacing-300);
    background-color: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-grey-300);
    border-radius: var(--cds-border-radius-100);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    box-sizing: border-box;
  `,
  authorName: css`
    margin: 0 0 var(--cds-spacing-100);
    font-weight: 700;
    color: var(--cds-color-neutral-primary);
  `,
  ratingLine: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--cds-spacing-50);
    margin: 0 0 var(--cds-spacing-200);
    color: var(--cds-color-neutral-primary);
  `,
  ratingDate: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
  dotSeparator: css`
    color: var(--cds-color-neutral-primary-weak);
    margin: 0 var(--cds-spacing-50);
  `,
  reviewText: css`
    margin: 0 0 var(--cds-spacing-100);
    color: var(--cds-color-neutral-primary);
  `,
};

type TopReviewCarouselCardProps = {
  topReview: TopReview;
};

const TopReviewCarouselCard = ({ topReview }: TopReviewCarouselCardProps) => {
  const { authorName, averageFiveStarRating, timestamp, comment } = topReview;

  const formattedDate = formatDateTimeDisplay(timestamp, MED_DATE_ONLY_DISPLAY);

  return (
    <article css={styles.card} data-testid="top-review-carousel-card">
      {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
      <Typography component="p" variant="bodyPrimary" css={styles.authorName}>
        {authorName}
      </Typography>
      <div css={styles.ratingLine}>
        <StarFilledIcon color="default" size="medium" />
        {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
        <Typography component="span" variant="subtitleSmall">
          {averageFiveStarRating.toFixed(1)}
        </Typography>
        <span css={styles.dotSeparator} aria-hidden="true">
          ·
        </span>
        {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
        <Typography component="span" variant="bodySecondary" css={styles.ratingDate}>
          <FormattedMessage message={_t('Reviewed {date}')} date={formattedDate} />
        </Typography>
      </div>
      {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
      <Typography component="p" variant="bodySecondary" css={styles.reviewText}>
        {comment}
      </Typography>
    </article>
  );
};

export default TopReviewCarouselCard;
